import React, {useCallback, useEffect, useMemo, useState} from "react";
import {del, get, post, put} from "../../api/api";
import {TemplateModel} from "../../models/TemplateModel";
import {
    Button,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Stack,
    Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TemplateNew from "./TemplateNew";
import TemplateView from "./TemplateView";
import {useAppStore} from "../../store/appStore";
import {showMessage} from "../../utils/snackbar";
import ShareTemplateDialog from "./share/ShareTemplateDialog"; // Import the new component
import UnshareTemplateDialog from "./share/UnshareTemplateDialog";
import {Role} from "../../models/UserModel";
import {hasRole} from "../../utils/roles"; // Import the new component
import Tooltip from "@mui/material/Tooltip";
import TemplateCreateDialog from "./TemplateCreateDialog";


const TemplateList: React.FC = () => {
    const templates = useAppStore((state) => state.templates);
    const userModel = useAppStore((state) => state.userModel);

    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [wizardLoading, setWizardLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<TemplateModel | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    const [sortColumn, setSortColumn] = useState<keyof TemplateModel>("name");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

    const [editingTemplate, setEditingTemplate] = useState<TemplateModel | null>(null);

    // State for share/unshare confirmation dialogs
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [unshareDialogOpen, setUnshareDialogOpen] = useState(false);
    const [templateToShare, setTemplateToShare] = useState<TemplateModel | null>(null);
    const hasTemplateAdmin = hasRole(userModel?.roles, Role.TEMPLATE_ADMIN) && !!userModel?.org_id

    const [newAction, setNewAction] = useState<string>("");

    const progressMessages = [
        "Bestand uploaden ...",
        "Document lezen ...",
        "De toverstaf erbij pakken ...",
        "10 punten aan Griffoendor geven ...",
        "Een slokje toverdrank nemen ...",
        "De toverspreuk uitspreken ...",
        "Bijna klaar ..."
    ];

    const [progressStep, setProgressStep] = useState(0);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        let secondTimer: NodeJS.Timeout | null = null;

        if (loading || wizardLoading) {
            setProgressStep(0);
            setElapsedSeconds(0);

            timer = setInterval(() => {
                setProgressStep((prev) => {
                    if (prev + 1 >= progressMessages.length) {
                        return progressMessages.length - 1;
                    }
                    return prev + 1;
                });
            }, 5000);

            secondTimer = setInterval(() => {
                setElapsedSeconds((prev) => prev + 1);
            }, 1000);
        } else {
            setProgressStep(0);
            setElapsedSeconds(0);
        }

        return () => {
            if (timer) clearInterval(timer);
            if (secondTimer) clearInterval(secondTimer);
        };
    }, [loading, wizardLoading]);

    useEffect(() => {
        const loadTemplates = async () => {
            setLoading(true);
            try {
                const fetchedTemplates = await get("/templates");
                const updatedTemplates = fetchedTemplates.map((template: any) => ({
                    ...template,
                    items: template.items.map((item: any) => ({
                        ...item,
                        id: item.id ?? Math.random().toString(36)
                    }))
                }));
                console.info(updatedTemplates)
                useAppStore.setState({templates: updatedTemplates || []});
            } catch (err) {
                console.warn("Error fetching templates:", err);
                showMessage("Er is een fout opgetreden bij het ophalen van de templates.", "error");
            } finally {
                setLoading(false);
            }
        };
        loadTemplates();
    }, []);

    const onTemplateUpdated = (updatedTemplate: TemplateModel) => {
        useAppStore.setState({
            templates: templates.map(t =>
                t.id === updatedTemplate.id ? updatedTemplate : t
            )
        });
    };

    const onDelete = async (templateId: string) => {
        if (window.confirm("Weet je zeker dat je deze template wilt verwijderen?")) {
            try {
                await del(`/templates/${templateId}`);
                useAppStore.setState({templates: templates.filter((t) => t.id !== templateId)});
            } catch (err) {
                console.warn("Error deleting template:", err);
                showMessage("Er is een fout opgetreden bij het verwijderen van de template.", "error");
            }
        }
    };

    const handleShare = async (template: TemplateModel) => {
        if (!userModel) return;
        if (!userModel.org_id) return;

        console.log(`Sharing template ${template}`);
        try {
            const sharedTemplate = await put(`/templates/${template.id}`, {...template, org_id: userModel.org_id,});
            onTemplateUpdated(sharedTemplate)
            showMessage(`Template "${template.name}" succesvol gedeeld.`, "success");
        } catch (err) {
            console.error("Error sharing template:", err);
            showMessage("Er is een fout opgetreden bij het delen van de template.", "error");
        }
    };

    const handleUnshare = async (template: TemplateModel) => {
        console.log(`Unsharing template ${template}`);
        try {
            const unSharedTemplate = await put(`/templates/${template.id}`, {...template, org_id: null,});
            onTemplateUpdated(unSharedTemplate);
            showMessage(`Delen van template "${template.name}" ongedaan gemaakt.`, "success");
        } catch (err) {
            console.error("Error unsharing template:", err);
            showMessage("Er is een fout opgetreden bij het ongedaan maken van het delen.", "error");
        }
    };


    const onCopy = async (templateToCopy: TemplateModel) => {
        try {
            const copy = {
                ...templateToCopy,
                name: `${templateToCopy.name} (kopie)`,
            };
            const newTemplate = await post("/templates", copy);
            useAppStore.setState({templates: [...templates, newTemplate]});
        } catch (err) {
            console.warn("Error copying template:", err);
            showMessage("Er is een fout opgetreden bij het kopiëren van de template.", "error");
        }
    };

    const onTemplateAdded = (newTemplate: TemplateModel) => {
        useAppStore.setState({templates: [...templates, newTemplate]});
    };

    const sortedTemplates = useMemo(() => {
        return [...templates].sort((a, b) => {
            const aValue = sortColumn === "items" ? a.items.length : a[sortColumn] ?? "";
            const bValue = sortColumn === "items" ? b.items.length : b[sortColumn] ?? "";

            if (typeof aValue === "string" && typeof bValue === "string") {
                return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
            }

            return 0;
        });
    }, [templates, sortColumn, sortOrder]);

    const filteredTemplates = useMemo(() => {
        const lowercasedSearchTerm = searchTerm.toLowerCase();

        return sortedTemplates.filter((template) =>
            Object.values(template).some((value) => {
                if (value === null || value === undefined) return false;
                return value.toString().toLowerCase().includes(lowercasedSearchTerm);
            })
        );
    }, [sortedTemplates, searchTerm]);

    const handleSort = useCallback(
        (column: keyof TemplateModel) => {
            setSortOrder((prev) => (sortColumn === column && prev === "asc" ? "desc" : "asc"));
            setSortColumn(column);
        },
        [sortColumn]
    );

    const handleNewAction = async (action: string, file?: File) => {
        if (action === "manual") {
            setIsCreating(true);
        } else if (action === "auto" && file) {
            try {
                setWizardLoading(true);
                const formData = new FormData();
                formData.append("file", file);
                const uploadedTemplate = await post("/templates/upload", formData);
                console.info("uploadedTemplate", uploadedTemplate);
                setEditingTemplate(uploadedTemplate);
            } catch (err) {
                const axiosError = err as import("axios").AxiosError;
                if (axiosError.response?.status === 413) {
                    showMessage("Het bestand is te groot. Probeer een bestand kleiner dan 1MB te uploaden.", "error");
                } else {
                    showMessage("Er is een fout opgetreden bij het uploaden van het document.", "error");
                }
                console.error("Error uploading template:", err);
            } finally {
                setWizardLoading(false);
            }
        }
        setNewAction(""); // Reset dropdown after action
    };

    if (loading) {
        return (
            <Paper
                sx={{
                    padding: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 200, 
                }}
            >
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ textAlign: "center" }}>
                    Bezig met laden...
                </Typography>
            </Paper>
        )
    }

    if (wizardLoading) {
        return (
            <Paper
                sx={{
                    padding: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 200,
                }}
            >
                <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}>
                Verstreken tijd: {formatTime(elapsedSeconds)} 
                <br/>
                Enkele minuten geduld alsjeblieft...
                <br/>
                </Typography>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ textAlign: "center", mb: 1 }}>
                    
                </Typography>
                <Typography variant="body1" sx={{ textAlign: "center", mb: 1 }}>
                    {progressMessages[progressStep]}
                </Typography>
                
            </Paper>
        )
    }

    if (isCreating) {
        return <TemplateNew
            onBack={() => setIsCreating(false)}
            onTemplateAdded={onTemplateAdded}
        />;
    }

    if (editingTemplate) {
        return <TemplateNew
            isEditing
            initialTemplate={editingTemplate}
            onBack={() => setEditingTemplate(null)}
            onTemplateUpdated={onTemplateUpdated}
            onTemplateAdded={onTemplateAdded}
            />;
        }

        if (selectedTemplate) {
            return <TemplateView
                template={selectedTemplate}
                onBack={() => setSelectedTemplate(null)}
            />;
        }


        return (
            <Container maxWidth="md">
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ mb: 2, justifyContent: "flex-end" }}
                >
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setNewAction("open-dialog")}
                    >
                        Nieuw
                    </Button>
                </Stack>
                <TemplateCreateDialog
                    open={newAction === "open-dialog"}
                    onClose={() => setNewAction("")}
                    onManual={() => {
                        setNewAction("");
                        setIsCreating(true);
                    }}
                    onWizard={async (file) => {
                        setNewAction("");
                        await handleNewAction("auto", file);
                    }}
                />
            <TextField
                fullWidth
                placeholder="Zoeken..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        ),
                    }
                }}
                sx={{marginBottom: 2}}
            />
            
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{fontWeight: 'bold'}}>
                                <TableSortLabel
                                    active={sortColumn === "name"}
                                    direction={sortOrder}
                                    onClick={() => handleSort("name")}
                                >
                                    Template
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>
                                <TableSortLabel
                                    active={sortColumn === "description"}
                                    direction={sortOrder}
                                    onClick={() => handleSort("description")}
                                >
                                    Beschrijving
                                </TableSortLabel>
                            </TableCell>

                            <TableCell sx={{fontWeight: 'bold'}}>
                                Acties
                            </TableCell>

                            {hasTemplateAdmin && (
                                <TableCell sx={{fontWeight: 'bold'}}>
                                    Delen
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTemplates.map((template) => {
                            return (
                                <TableRow
                                    key={template.id}
                                    sx={
                                        template.permission === "write" ? {
                                            borderLeft: `4px solid #20A4A9`,
                                            cursor: 'pointer'
                                        } : {
                                            cursor: 'pointer'
                                        }
                                    }
                                    onClick={() => setSelectedTemplate(template)}
                                >
                                    <TableCell>{template.name}</TableCell>
                                    <TableCell>{template.description}</TableCell>
                                    {/* Conditionally render the Share switch cell */}

                                    <TableCell sx={{width: "210px"}}>
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCopy(template)
                                            }}
                                            title="Kopiëren">
                                            <ContentCopyIcon/>
                                        </IconButton>

                                        {/* Write Permission Actions */}
                                        {template.permission === "write" && (
                                            <>
                                                <IconButton onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingTemplate(template)
                                                }}
                                                            title="Bewerken">
                                                    <EditIcon/>
                                                </IconButton>

                                                {!template.org_id && <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDelete(template.id)
                                                    }}
                                                    color="error"
                                                    title="Verwijderen"
                                                >
                                                    <DeleteIcon/>
                                                </IconButton>}
                                            </>
                                        )}
                                    </TableCell>
                                    {hasTemplateAdmin && (
                                        <TableCell onClick={(e) => e.stopPropagation()} sx={{width: "80px"}}>
                                            <Switch
                                                checked={template.org_id != null}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    setTemplateToShare(template);
                                                    if (e.target.checked) {
                                                        setShareDialogOpen(true);
                                                    } else {
                                                        setUnshareDialogOpen(true);
                                                    }
                                                }}
                                                disabled={template.permission !== 'write'}
                                                title={template.org_id ? "Gedeeld met organisatie" : "Niet gedeeld"}
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <ShareTemplateDialog
                open={shareDialogOpen}
                onClose={() => {
                    setShareDialogOpen(false);
                    setTemplateToShare(null); // Clear template when closing
                }}
                onConfirm={() => {
                    if (templateToShare) {
                        handleShare(templateToShare);
                    }
                }}
                templateName={templateToShare?.name}
            />

            <UnshareTemplateDialog
                open={unshareDialogOpen}
                onClose={() => {
                    setUnshareDialogOpen(false);
                    setTemplateToShare(null); // Clear template when closing
                }}
                onConfirm={() => {
                    if (templateToShare) {
                        handleUnshare(templateToShare);
                    }
                }}
                templateName={templateToShare?.name}
            />
        </Container>
    );
};

export default TemplateList;
